<template>
    <div class="customer-page-header">
        <div class="search-filter-section">
            <div class="search-box">
                <div class="input-with-icon">
                    <i class="material-icons search-icon">search</i>
                    <input
                        type="text"
                        v-model="searchValue"
                        placeholder="Search customers..."
                        class="form-control search-input"
                        @keyup.enter="$emit('search')"
                        @input="$emit('search-input')"
                    />
                    <button v-if="searchStr" class="clear-search" type="button" @click="$emit('clear-search')">
                        <i class="material-icons">close</i>
                    </button>
                </div>
            </div>

            <div class="search-type-selector" v-if="searchStr">
                <select class="form-select form-select-sm" v-model="searchTypeValue" @change="$emit('search-type-change')">
                    <option v-for="option in filterOptions.searchType" :key="option.value" :value="option.value">
                        {{ option.label }}
                    </option>
                </select>
            </div>

            <div class="filter-options">
                <div class="dropdown">
                    <button class="btn dropdown-toggle filter-btn" type="button" id="filterDropdown" data-bs-toggle="dropdown" aria-expanded="false">
                        <i class="material-icons">filter_list</i>
                        Filters
                    </button>
                    <div class="dropdown-menu dropdown-menu-end filter-dropdown p-3" aria-labelledby="filterDropdown">
                        <h6 class="filter-title mb-2">Filter Customers</h6>

                        <div class="filter-grid">
                            <div class="filter-column">
                                <div class="mb-2">
                                    <label class="filter-label">Customer Group</label>
                                    <select class="form-select form-select-sm" :value="filters.groupId" @change="updateFilter('groupId', $event.target.value)">
                                        <option value="">All Groups</option>
                                        <option v-for="group in customerGroups" :key="group.id" :value="group.id">
                                            {{ group.name }}
                                        </option>
                                    </select>
                                </div>

                                <div class="mb-2">
                                    <label class="filter-label">Status</label>
                                    <select class="form-select form-select-sm" :value="filters.status" @change="updateFilter('status', $event.target.value)">
                                        <option v-for="option in filterOptions.status" :key="option.value" :value="option.value">
                                            {{ option.label }}
                                        </option>
                                    </select>
                                </div>

                                <div class="mb-2" v-if="showLoyalty">
                                    <label class="filter-label">Current Points Range</label>
                                    <div class="points-range">
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            class="form-control form-control-sm"
                                            :value="filters.minPoints"
                                            placeholder="Minimum"
                                            aria-label="Minimum current points"
                                            @input="updateFilter('minPoints', $event.target.value)"
                                        />
                                        <span>to</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            class="form-control form-control-sm"
                                            :value="filters.maxPoints"
                                            placeholder="Maximum"
                                            aria-label="Maximum current points"
                                            @input="updateFilter('maxPoints', $event.target.value)"
                                        />
                                    </div>
                                </div>

                                <div class="mb-2">
                                    <label class="filter-label">Secondary Sort</label>
                                    <select class="form-select form-select-sm" v-model="secondarySortFieldValue">
                                        <option value="">None</option>
                                        <option v-for="field in secondarySortOptions" :key="field" :value="field">
                                            {{ formatSortFieldLabel(field) }}
                                        </option>
                                    </select>
                                </div>
                            </div>

                            <div class="filter-column">
                                <div class="mb-2">
                                    <label class="filter-label">Date Range</label>
                                    <div class="date-range-compact">
                                        <input
                                            type="date"
                                            class="form-control form-control-sm"
                                            :value="filters.dateFrom"
                                            placeholder="From"
                                            @input="updateFilter('dateFrom', $event.target.value)"
                                        />
                                        <input
                                            type="date"
                                            class="form-control form-control-sm mt-1"
                                            :value="filters.dateTo"
                                            placeholder="To"
                                            @input="updateFilter('dateTo', $event.target.value)"
                                        />
                                    </div>
                                </div>

                                <div class="mb-2">
                                    <label class="filter-label">ID Verification</label>
                                    <select class="form-select form-select-sm" :value="filters.verified" @change="updateFilter('verified', $event.target.value)">
                                        <option v-for="option in filterOptions.verified" :key="option.value" :value="option.value">
                                            {{ option.label }}
                                        </option>
                                    </select>
                                </div>

                                <div class="mb-2">
                                    <label class="filter-label">Sort By</label>
                                    <select class="form-select form-select-sm" v-model="sortFieldValue">
                                        <option v-for="field in availableSortFields" :key="field" :value="field">
                                            {{ formatSortFieldLabel(field) }}
                                        </option>
                                    </select>
                                    <div class="sort-direction mt-1">
                                        <div class="form-check form-check-inline">
                                            <input class="form-check-input" type="radio" v-model="sortDirectionValue" value="asc" id="sortAsc">
                                            <label class="form-check-label small" for="sortAsc">Asc</label>
                                        </div>
                                        <div class="form-check form-check-inline">
                                            <input class="form-check-input" type="radio" v-model="sortDirectionValue" value="desc" id="sortDesc">
                                            <label class="form-check-label small" for="sortDesc">Desc</label>
                                        </div>
                                    </div>
                                </div>

                                <div class="mb-2" v-if="secondarySortField">
                                    <label class="filter-label">Secondary Direction</label>
                                    <div class="sort-direction">
                                        <div class="form-check form-check-inline">
                                            <input class="form-check-input" type="radio" v-model="secondarySortDirectionValue" value="asc" id="secondarySortAsc">
                                            <label class="form-check-label small" for="secondarySortAsc">Asc</label>
                                        </div>
                                        <div class="form-check form-check-inline">
                                            <input class="form-check-input" type="radio" v-model="secondarySortDirectionValue" value="desc" id="secondarySortDesc">
                                            <label class="form-check-label small" for="secondarySortDesc">Desc</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="d-flex justify-content-between mt-3">
                            <button class="btn btn-outline-secondary btn-sm" type="button" @click="$emit('reset-sort')">
                                Reset
                            </button>
                            <button class="btn btn-primary btn-sm" type="button" @click="$emit('apply-filters')">
                                Apply Filters
                            </button>
                        </div>
                    </div>
                </div>

                <div class="page-size-selector">
                    <span class="me-2">Show:</span>
                    <select class="form-select form-select-sm page-size-select" v-model="limitValue" @change="$emit('limit-change')">
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>
            </div>
        </div>

        <div class="action-buttons">
            <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_REPORT_CUSTOMER">
                <button class="btn btn-secondary action-btn" type="button" :disabled="exporting" @click="$emit('export')">
                    <i class="material-icons">{{ exporting ? "hourglass_top" : "download" }}</i>
                    {{ exporting ? "Exporting..." : "Export Customers" }}
                </button>
            </AllowPermission>
            <AllowPermission :permission="PERMISSION_CODES.CMS_CUSTOMER_IMPORT">
                <button class="btn btn-secondary action-btn" type="button" @click="$router.push(ROUTE_PATHS.CUSTOMERS_IMPORT)">
                    <i class="material-icons">upload_file</i>
                    Import Customers
                </button>
            </AllowPermission>
            <AllowPermission :permission="PERMISSION_CODES.CMS_CREATE_CUSTOMER">
                <button class="btn btn-primary action-btn" type="button" @click="$router.push(ROUTE_PATHS.CUSTOMER_CREATE)">
                    <i class="material-icons">add</i>
                    Create Customer
                </button>
            </AllowPermission>
        </div>
    </div>
</template>

<script>
import AllowPermission from "../layout/AllowPermission.vue"
import { PERMISSION_CODES } from "../../store/permission"
import { ROUTE_PATHS } from "../../router/route-paths"

export default {
    components: { AllowPermission },
    props: {
        searchStr: { type: String, default: "" },
        searchType: { type: String, default: "" },
        filterOptions: { type: Object, required: true },
        filters: { type: Object, required: true },
        customerGroups: { type: Array, default: () => [] },
        secondarySortField: { type: String, default: "" },
        secondarySortDirection: { type: String, default: "asc" },
        sortField: { type: String, default: "createdAt" },
        sortDirection: { type: String, default: "desc" },
        availableSortFields: { type: Array, default: () => [] },
        showLoyalty: { type: Boolean, default: false },
        exporting: { type: Boolean, default: false },
        limit: { type: [Number, String], default: 25 },
        formatSortFieldLabel: { type: Function, required: true },
    },
    data() {
        return {
            PERMISSION_CODES,
            ROUTE_PATHS,
        }
    },
    computed: {
        searchValue: {
            get() {
                return this.searchStr
            },
            set(value) {
                this.$emit("update:searchStr", value)
            },
        },
        searchTypeValue: {
            get() {
                return this.searchType
            },
            set(value) {
                this.$emit("update:searchType", value)
            },
        },
        secondarySortFieldValue: {
            get() {
                return this.secondarySortField
            },
            set(value) {
                this.$emit("update:secondarySortField", value)
            },
        },
        secondarySortDirectionValue: {
            get() {
                return this.secondarySortDirection
            },
            set(value) {
                this.$emit("update:secondarySortDirection", value)
            },
        },
        sortFieldValue: {
            get() {
                return this.sortField
            },
            set(value) {
                this.$emit("update:sortField", value)
            },
        },
        sortDirectionValue: {
            get() {
                return this.sortDirection
            },
            set(value) {
                this.$emit("update:sortDirection", value)
            },
        },
        limitValue: {
            get() {
                return this.limit
            },
            set(value) {
                this.$emit("update:limit", Number(value))
            },
        },
        secondarySortOptions() {
            return this.availableSortFields.filter((field) => field !== this.sortField)
        },
    },
    methods: {
        updateFilter(field, value) {
            this.$emit("update-filter", { field, value })
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.customer-page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $sectionGap;
    gap: 12px;

    .search-filter-section {
        display: flex;
        align-items: center;
        gap: $sectionGap;
        flex: 1;

        .search-box {
            flex: 1;
            max-width: 400px;

            .input-with-icon {
                position: relative;

                .search-icon {
                    position: absolute;
                    left: 10px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: $textColor;
                    font-size: 18px;
                    opacity: 0.7;
                }

                .search-input {
                    padding-left: 36px;
                    border-radius: $cardRadius - 6px;
                    border: 1px solid $lightBorderColor;
                    font-size: 14px;
                    height: $component-height;

                    &:focus {
                        border-color: $primaryColor;
                        box-shadow: 0 0 0 3px rgba($primaryColor, 0.1);
                    }
                }

                .clear-search {
                    position: absolute;
                    right: 8px;
                    top: 50%;
                    transform: translateY(-50%);
                    border: none;
                    background: none;
                    padding: 0;
                    cursor: pointer;
                    color: $textColor;
                    opacity: 0.5;

                    &:hover {
                        opacity: 0.8;
                    }

                    i {
                        font-size: 16px;
                    }
                }
            }
        }

        .search-type-selector {
            flex: 1;
            max-width: 200px;

            select {
                border: 1px solid $lightBorderColor;
                border-radius: 4px;
                background-color: $lightCardBg;
                padding: 2px 6px;
                font-size: 14px;
                color: $textColor;
            }
        }

        .filter-options {
            display: flex;
            align-items: center;
            gap: 12px;

            .filter-btn {
                display: flex;
                align-items: center;
                gap: 6px;
                background-color: $lightHoverColor;
                border: 1px solid $lightBorderColor;
                color: $textColor;
                font-size: 14px;
                font-weight: 500;
                padding: 6px 12px;
                border-radius: 6px;

                &:hover {
                    background-color: lighten($lightHoverColor, 2%);
                }

                i {
                    font-size: 16px;
                }
            }

            .filter-dropdown {
                width: 500px;
                max-height: 500px;
                overflow-y: auto;
                padding: $cardPaddingY;
                border-radius: $cardRadius;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                border: 1px solid $lightBorderColor;

                .filter-title {
                    color: $primaryColor;
                    font-weight: 600;
                    border-bottom: 1px solid $lightBorderColor;
                    padding-bottom: 8px;
                    margin-bottom: 12px;
                }

                .filter-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 12px;

                    .filter-column {
                        .filter-label {
                            font-size: 12px;
                            font-weight: 600;
                            color: $textColor;
                            margin-bottom: 4px;
                            display: block;
                        }

                        .date-range-compact {
                            display: flex;
                            flex-direction: column;
                        }

                        .sort-direction {
                            .form-check-inline {
                                margin-right: 8px;
                            }

                            .small {
                                font-size: 12px;
                            }

                            .form-check-input:checked {
                                background-color: $primaryColor;
                                border-color: $primaryColor;
                            }
                        }
                    }
                }

                .btn-primary {
                    background-color: $primaryColor;
                    border-color: darken($primaryColor, 5%);

                    &:hover {
                        background-color: darken($primaryColor, 5%);
                    }
                }

                .btn-outline-secondary {
                    border-color: $lightBorderColor;
                    color: $textColor;

                    &:hover {
                        background-color: $lightHoverColor;
                    }
                }
            }

            .page-size-selector {
                display: flex;
                align-items: center;

                span {
                    font-size: 14px;
                    color: $textColor;
                }

                select {
                    border: 1px solid $lightBorderColor;
                    border-radius: 4px;
                    background-color: $lightCardBg;
                    padding: 2px 6px;
                    font-size: 14px;
                    color: $textColor;
                }

                .page-size-select {
                    min-width: 50px;
                    padding-right: 24px;
                }
            }
        }
    }

    .action-buttons {
        display: flex;
        gap: 10px;

        .action-btn {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            padding: 6px 12px;
            border-radius: 6px;
            font-weight: 500;

            i {
                font-size: 16px;
            }
        }

        .btn-secondary {
            background-color: $lightHoverColor;
            border: 1px solid $lightBorderColor;
            color: $textColor;

            &:hover {
                background-color: darken($lightHoverColor, 5%);
            }
        }

        .btn-primary {
            background-color: $primaryColor;
            border: 1px solid darken($primaryColor, 5%);
            color: white;

            &:hover {
                background-color: darken($primaryColor, 5%);
            }
        }
    }
}

.points-range {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 6px;

    span {
        color: rgba($textColor, 0.65);
        font-size: 12px;
    }
}

:deep(.dark) {
    .input-with-icon {
        .search-icon,
        .clear-search {
            color: $darkTextColor;
        }

        .search-input {
            background-color: $darkCardBg;
            border-color: $darkBorderColor;
            color: $darkTextColor;

            &:focus {
                border-color: $secondaryColorDark;
                box-shadow: 0 0 0 3px rgba($secondaryColorDark, 0.2);
            }
        }
    }

    .filter-btn {
        background-color: rgba(255, 255, 255, 0.05);
        border-color: $darkBorderColor;
        color: $darkTextColor;

        &:hover {
            background-color: rgba(255, 255, 255, 0.08);
        }
    }

    .filter-dropdown {
        background-color: $darkCardBg;
        border-color: $darkBorderColor;

        .filter-title {
            color: $darkTextColor;
            border-color: $darkBorderColor;
        }

        .filter-column {
            .filter-label {
                color: $darkTextColor;
            }

            .form-select,
            .form-control {
                background-color: rgba(255, 255, 255, 0.05);
                border-color: $darkBorderColor;
                color: $darkTextColor;

                &:focus {
                    border-color: $secondaryColorDark;
                    box-shadow: 0 0 0 0.2rem rgba($secondaryColorDark, 0.25);
                }
            }

            .sort-direction {
                .form-check-label {
                    color: $darkTextColor;
                }

                .form-check-input:checked {
                    background-color: $secondaryColorDark;
                    border-color: $secondaryColorDark;
                }
            }
        }

        .btn-primary {
            background-color: $secondaryColorDark;
            border-color: darken($secondaryColorDark, 5%);

            &:hover {
                background-color: darken($secondaryColorDark, 5%);
            }
        }

        .btn-outline-secondary {
            border-color: $darkBorderColor;
            color: $darkTextColor;

            &:hover {
                background-color: rgba(255, 255, 255, 0.08);
            }
        }

        .filter-grid .filter-column .filter-label {
            color: $darkTextColor !important;
        }
        label.filter-label {
            color: $darkTextColor !important;
        }
    }

    .page-size-selector {
        select {
            background-color: $darkCardBg;
            border-color: $darkBorderColor;
            color: $darkTextColor;
        }

        span {
            color: $darkTextColor;
        }
    }
}

@media (max-width: 768px) {
    .customer-page-header {
        flex-direction: column;

        .search-filter-section {
            width: 100%;
            flex-direction: column;
            align-items: flex-start;

            .search-box {
                max-width: 100%;
                width: 100%;
            }

            .filter-options {
                width: 100%;
                justify-content: space-between;

                .filter-dropdown {
                    width: 100%;
                    max-width: 100%;

                    .filter-grid {
                        grid-template-columns: 1fr;
                        gap: 8px;
                    }
                }
            }
        }

        .action-buttons {
            width: 100%;
            justify-content: space-between;
        }
    }
}
</style>
